import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart3 } from "lucide-react";

export default function Reports() {
  const { data: monthlyPnl, isLoading } = trpc.records.monthlyPnl.useQuery();

  const totalPnl = monthlyPnl?.reduce((s, m) => s + (m.totalPnl || 0), 0) || 0;
  const totalTrades = monthlyPnl?.reduce((s, m) => s + (m.tradeCount || 0), 0) || 0;
  const totalAmount = monthlyPnl?.reduce((s, m) => s + (m.totalAmount || 0), 0) || 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          盈亏报表
        </h1>
        <p className="text-sm text-muted-foreground mt-1">按月统计交易盈亏情况</p>
      </div>

      {/* 汇总 */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-card border-border/50">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">累计盈亏</p>
            <p className={`text-xl font-bold ${totalPnl >= 0 ? "text-gain" : "text-loss"}`}>
              {totalPnl >= 0 ? "+" : ""}{totalPnl.toFixed(0)} 元
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">累计交易次数</p>
            <p className="text-xl font-bold">{totalTrades} 次</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">累计交易金额</p>
            <p className="text-xl font-bold">{(totalAmount / 10000).toFixed(1)} 万</p>
          </CardContent>
        </Card>
      </div>

      {/* 月度盈亏图表 */}
      {monthlyPnl && monthlyPnl.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">月度盈亏走势</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px] flex items-end gap-2 px-4">
              {[...monthlyPnl].reverse().map((m, i) => {
                const maxAbs = Math.max(...monthlyPnl.map(x => Math.abs(x.totalPnl || 0)), 1);
                const height = Math.abs(m.totalPnl || 0) / maxAbs * 160;
                const isPositive = (m.totalPnl || 0) >= 0;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="flex-1 flex items-end w-full">
                      <div
                        className={`w-full rounded-t ${isPositive ? "bg-gain" : "bg-loss"}`}
                        style={{ height: `${Math.max(height, 4)}px` }}
                        title={`${m.month}: ${(m.totalPnl || 0) >= 0 ? "+" : ""}${(m.totalPnl || 0).toFixed(0)} 元`}
                      />
                    </div>
                    <span className="text-[10px] text-muted-foreground">{m.month?.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 月度明细 */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">月度明细</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {monthlyPnl && monthlyPnl.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50">
                  <TableHead className="text-xs">月份</TableHead>
                  <TableHead className="text-xs text-right">交易次数</TableHead>
                  <TableHead className="text-xs text-right">交易金额</TableHead>
                  <TableHead className="text-xs text-right">盈亏</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyPnl.map((m, i) => (
                  <TableRow key={i} className="border-border/30">
                    <TableCell className="text-xs font-medium">{m.month}</TableCell>
                    <TableCell className="text-xs text-right">{m.tradeCount}</TableCell>
                    <TableCell className="text-xs text-right">{((m.totalAmount || 0) / 10000).toFixed(1)} 万</TableCell>
                    <TableCell className={`text-xs text-right font-medium ${(m.totalPnl || 0) >= 0 ? "text-gain" : "text-loss"}`}>
                      {(m.totalPnl || 0) >= 0 ? "+" : ""}{(m.totalPnl || 0).toFixed(0)} 元
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <BarChart3 className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">{isLoading ? "加载中..." : "暂无报表数据"}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
